import React, { useState } from 'react';
import { FormField } from '@/components/FormField';
import { Card, SectionHeader } from '@/components/Card';
import { Info, MapPin, Globe, Smartphone, MessageSquare, MessageCircle, Camera } from 'lucide-react';
import { BaseAdSetForm, Seg } from './BaseAdSetForm';
import { AttributionWindowSection, buildAttributionSpec } from './AttributionWindowSection';
import { goalsForLocation, GOAL_LABEL, ConversionLocation } from './odaxMatrix';
import {
  FbPagePicker,
  IgAccountPicker,
  WhatsAppNumberPicker,
  FbAppPicker,
  type PlacementValue,
} from '@/components/facebook/MetaResourcePickers';
import type { MetaApp } from '@/services/meta-resources.service';
import { appOsTargeting } from './appTargeting';

const DEST_TYPES = [
  { v: 'WEBSITE',          l: 'Website',      icon: <Globe size={13} /> },
  { v: 'APP',              l: 'App',          icon: <Smartphone size={13} /> },
  { v: 'MESSENGER',        l: 'Messenger',    icon: <MessageSquare size={13} /> },
  { v: 'WHATSAPP',         l: 'WhatsApp',     icon: <MessageCircle size={13} /> },
  { v: 'INSTAGRAM_DIRECT', l: 'Instagram DM', icon: <Camera size={13} /> },
];

/**
 * Map our destType (UI value, also Meta's `destination_type` enum) to the
 * matrix's ConversionLocation. INSTAGRAM_DIRECT isn't in Meta's published
 * Traffic matrix; we treat it like Messenger (same goal set) since it's
 * the same kind of destination.
 */
function destTypeToLocation(dt: string): ConversionLocation {
  switch (dt) {
    case 'WEBSITE':          return 'WEBSITE';
    case 'APP':              return 'APP';
    case 'MESSENGER':        return 'MESSENGER';
    case 'WHATSAPP':         return 'WHATSAPP';
    case 'INSTAGRAM_DIRECT': return 'MESSENGER'; // closest cell — same goals
    default:                 return 'WEBSITE';
  }
}


export const TrafficAdSetForm: React.FC = () => {
  const [destType, setDestType] = useState('WEBSITE');
  const [pageId, setPageId] = useState('');
  const [igActorId, setIgActorId] = useState<string | null>(null);
  const [waPhoneId, setWaPhoneId] = useState('');
  const [waPhoneDigits, setWaPhoneDigits] = useState('');
  // App destination
  const [fbAppId, setFbAppId] = useState('');
  const [pickedApp, setPickedApp] = useState<MetaApp | null>(null);
  // Object-store URL is auto-derived from the picked app's metadata (no manual
  // entry — most users wouldn't know which store URL Meta wants).
  const objectStoreUrl =
    pickedApp?.objectStoreUrls?.google_play ??
    pickedApp?.objectStoreUrls?.iphone_app_store ??
    pickedApp?.objectStoreUrls?.amazon_app_store ??
    '';
  // Mirror of placement.publisher_platforms so we can disable IG picker when
  // 'instagram' is excluded. BaseAdSetForm calls onPlacementChange on mount
  // and on every edit.
  const [placement, setPlacement] = useState<PlacementValue>({ mode: 'automatic' });
  // Attribution windows — Meta only ACCEPTS 1d click for OUTCOME_TRAFFIC goals
  // (live 2026-08-28: 7d click is rejected with "supported values is/are
  // attribution window of 1 day(s)", subcode 1885423). The old 7d default
  // broke every UI-built Traffic ad set.
  const [clickWin, setClickWin] = useState('1');
  const [viewWin, setViewWin] = useState('0');

  // Goal options filter by the chosen destination per Meta's ODAX matrix
  // (e.g. APP destination only allows LINK_CLICKS + REACH; LANDING_PAGE_VIEWS
  // is rejected). Recompute whenever destType changes.
  const goalOptions = goalsForLocation('OUTCOME_TRAFFIC', destTypeToLocation(destType))
    .map((g) => ({ code: g, label: GOAL_LABEL[g] ?? g }));

  const igAllowed =
    placement.mode === 'automatic' ||
    !placement.publisher_platforms ||
    placement.publisher_platforms.includes('instagram');

  const extraPayload = () => {
    const p: Record<string, unknown> = { destination_type: destType };
    const promoted: Record<string, string> = {};
    if (pageId) promoted.page_id = pageId;

    // Click-to-WhatsApp takes the Page plus the number's digits. The old hard-coded WhatsApp application_id
    // was refused with "(#900) invalid app id" on every save (live, 2026-09-29).
    if (destType === 'WHATSAPP' && waPhoneDigits) promoted.whatsapp_phone_number = waPhoneDigits;

    if (destType === 'APP') {
      if (fbAppId) promoted.application_id = fbAppId;
      if (objectStoreUrl) promoted.object_store_url = objectStoreUrl;
    }

    // MESSENGER + INSTAGRAM_DIRECT just need destination_type + page_id; the
    // click-to-thread CTA is set on the ad creative.

    if (Object.keys(promoted).length > 0) p.promoted_object = promoted;
    // instagram_actor_id belongs on the ad creative, not the ad set — Meta
    // rejects it here and the workspace DTO doesn't whitelist it. The chosen
    // IG account flows through the creative builder instead.
    p.attribution_spec = buildAttributionSpec(clickWin, viewWin);
    return p;
  };

  const extraTargeting = () => (destType === 'APP' ? appOsTargeting(objectStoreUrl) : undefined);

  const validateExtra = () => {
    if (!pageId) return 'Pick a Facebook Page first';
    if (destType === 'WHATSAPP' && !waPhoneId) return 'Pick a WhatsApp number';
    if (destType === 'APP' && !fbAppId) return 'Pick a Facebook App for the App destination';
    if (destType === 'INSTAGRAM_DIRECT' && !igActorId) return 'Pick the Instagram account that will receive DMs';
    return null;
  };

  return (
    <BaseAdSetForm
      optimizationGoals={goalOptions}
      extraPayload={extraPayload}
      extraTargeting={extraTargeting}
      validateExtra={validateExtra}
      onPlacementChange={setPlacement}
      expectedObjective="OUTCOME_TRAFFIC"
    >
      <FormField label="Destination type">
        <Seg options={DEST_TYPES} value={destType} onChange={setDestType} />
      </FormField>

      {destType === 'WHATSAPP' && (
        <FormField label="WhatsApp number" required hintBelow hint="One of your saved WhatsApp business numbers">
          <WhatsAppNumberPicker
            value={waPhoneId}
            onChange={(id, num) => { setWaPhoneId(id); setWaPhoneDigits((num?.displayPhoneNumber ?? '').replace(/\D/g, '')); }}
          />
        </FormField>
      )}

      {destType === 'APP' && (
        <Card padded className="!mt-3 bg-info-soft/40">
          <div className="flex items-start gap-2 mb-3 text-xs">
            <Info size={14} className="mt-0.5 shrink-0 text-info" />
            <p className="text-text-secondary">
              For dedicated app installs, the <strong>App Promotions</strong>{' '}
              objective gives you better optimization. App-as-traffic-destination
              is supported but uncommon.
            </p>
          </div>
          <FormField label="Facebook App" required hintBelow hint="Pick from apps you've registered at developers.facebook.com.">
            <FbAppPicker
              value={fbAppId}
              mobileOnly
              onChange={(id, app) => { setFbAppId(id); setPickedApp(app); }}
            />
          </FormField>
        </Card>
      )}

      {destType === 'MESSENGER' && (
        <div className="flex items-start gap-2 rounded-lg border border-info/40 bg-info-soft px-3 py-2.5 text-xs">
          <Info size={14} className="mt-0.5 shrink-0 text-info" />
          <p className="text-text-secondary">
            Click-to-Messenger uses your Facebook Page's Messenger thread. On
            the next step, set the ad creative's CTA to <strong>Message Page</strong>. Replies arrive in your Page inbox in Meta Business Suite — SlightorCRM has no Messenger or Instagram DM inbox yet.
          </p>
        </div>
      )}

      {destType === 'INSTAGRAM_DIRECT' && (
        <div className="flex items-start gap-2 rounded-lg border border-info/40 bg-info-soft px-3 py-2.5 text-xs">
          <Info size={14} className="mt-0.5 shrink-0 text-info" />
          <p className="text-text-secondary">
            Click-to-Instagram-DM requires an Instagram business account
            connected to your Page (picker below) and a Messenger-style CTA on
            the ad creative. Replies arrive in your Page inbox in Meta Business Suite — SlightorCRM has no Messenger or Instagram DM inbox yet.
          </p>
        </div>
      )}

      <Card padded className="!mt-5">
        <SectionHeader
          title="Where ads will run"
          icon={<MapPin size={16} />}
          subtitle="Pick the Facebook Page (required) and any Instagram you manage (optional)"
        />
        <FormField label="Facebook Page" required>
          <FbPagePicker
            value={pageId}
            restrictToAuthorized
            onChange={(id) => setPageId(id)}
          />
        </FormField>
        <FormField
          label={destType === 'INSTAGRAM_DIRECT' ? 'Instagram (required for IG DM)' : 'Instagram (optional)'}
          required={destType === 'INSTAGRAM_DIRECT'}
        >
          <IgAccountPicker
            value={igActorId}
            onChange={(id) => setIgActorId(id)}
            disabled={!igAllowed}
            disabledReason="Instagram is excluded from your Placements selection. Add Instagram under Manual placements to enable this."
          />
        </FormField>
      </Card>

      <AttributionWindowSection
        clickWin={clickWin}
        setClickWin={setClickWin}
        viewWin={viewWin}
        setViewWin={setViewWin}
        clickOptions={['1']}
        hideViewThrough
        hint="Traffic campaigns only accept a 1-day click window — Meta rejects everything else."
      />
    </BaseAdSetForm>
  );
};
